import { options } from "../components/InsuranceForm/util"
import { getProvider } from "."
import api from "../../../../../api/axios"

/**
 * End date for one covered person. Keeps the end date already set unless it breaks
 * our rules for this start date; otherwise uses the default for the insurance type:
 * individual coverage and Medicare end Dec 31 of the start year, group and other
 * coverage are ongoing (no end date).
 */
export function getCoveredEndDate(insuranceType, startDate, endDate) {
  if (!startDate) {
    return null
  }
  const yearEndDate = `${startDate.slice(0, 4)}-12-31`
  const endsAtYearEnd = insuranceType === "INDIVIDUAL" || insuranceType === "MEDICARE"
  const breaksRules =
    !endDate || endDate < startDate || (endsAtYearEnd && endDate > yearEndDate)
  if (!breaksRules) {
    return endDate
  }
  return endsAtYearEnd ? yearEndDate : null
}

/**
 * Saves the user's health insurance (health insurance v2).
 *   new:      POST  /user/healthInsurance
 *   existing: PATCH /user/healthInsurance/:userHealthInsuranceId
 *
 * Insurance belongs to the user. The plan is only used for the coverage
 * choices (by plan type). The insurance has no dates of its own; each
 * covered person has their own start date and end date.
 */
export default async function submitInsurance(values, user, plan) {
  let { insuranceCoverage, provider, policyId, policyHolder, id } = values
  let { coveredIndividuals, isComplete } = values
  const userHealthInsuranceId = id
  insuranceCoverage = options.insuranceCoverages(plan?.type)?.[insuranceCoverage]
  const insuranceType = insuranceCoverage?.insuranceType

  if (!provider?.id && provider?.name) {
    provider = await getProvider(provider)
  }

  const payload = {
    insuranceType,
    includesMedical: insuranceCoverage?.includesMedical,
    includesDental: insuranceCoverage?.includesDental,
    includesVision: insuranceCoverage?.includesVision,
    onExchange: insuranceCoverage?.onExchange,
    includesMedicarePartA: insuranceCoverage?.includesMedicarePartA,
    includesMedicarePartB: insuranceCoverage?.includesMedicarePartB,
    includesMedicarePartC: insuranceCoverage?.includesMedicarePartC,
    includesMedicarePartD: insuranceCoverage?.includesMedicarePartD,
    includesMedigap: insuranceCoverage?.includesMedigap,
    premiumReimbursementTaxable: insuranceCoverage?.premiumReimbursementTaxable,
    providerId: provider?.id,
    policyId,
    policyHolderId: policyHolder || user?.id,
    // Each covered person with their own first month of coverage and end date
    userHealthInsuranceCoveredIndividuals: (coveredIndividuals || []).map((coveredIndividual) => {
      const startDate = coveredIndividual?.startDate || undefined
      return {
        coveredIndividualId: coveredIndividual?.id ?? coveredIndividual,
        startDate,
        endDate: getCoveredEndDate(insuranceType, startDate, coveredIndividual?.endDate),
      }
    }),
    isComplete: Boolean(isComplete),
  }

  const response = userHealthInsuranceId
    ? await api.patch(`/user/healthInsurance/${userHealthInsuranceId}`, payload)
    : await api.post("/user/healthInsurance", payload)
  return response?.data
}